import { FieldHookConfig, useField, useFormikContext } from 'formik';
import {
  checkFormHasError,
  getFormHelperText,
  handleBlurFormFormattedTextField,
  handleChangeFormFormattedTextField,
} from '@tock/shared/ui/forms/formHelpers';
import { omit, toString } from 'es-toolkit/compat';
import FormattedTextField, {
  FormattedTextFieldProps,
} from 'omakase/FormControls/FormattedTextField';
import React from 'react';

/**
 * Formik-ready text input that filters text based on a supplied {@link InputFilter}
 */
const FormFormattedTextField: React.FunctionComponent<FormattedTextFieldProps> = ({
  helperText,
  onChange,
  onBlur,
  filter,
  ...rest
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [fieldConfig, meta] = useField(rest as FieldHookConfig<any>);
  const context = useFormikContext<unknown>();

  React.useEffect(() => {
    context.setFieldValue(
      fieldConfig.name,
      toString(filter.parseViewValue(fieldConfig.value)),
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const idValue = rest.id ?? rest.name;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    handleChangeFormFormattedTextField(
      event,
      fieldConfig.name,
      filter,
      context.setFieldValue,
      onChange
    );

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) =>
    handleBlurFormFormattedTextField(
      event,
      fieldConfig.name,
      filter,
      context.setFieldValue,
      context.setFieldTouched,
      onBlur
    );

  const hasError = checkFormHasError(meta);
  const helperTextValue = getFormHelperText(hasError, meta, helperText);

  // Destructuring out onChange and onBlur to prevent them from being declared twice on TextField
  const trimmedFieldConfig = omit(fieldConfig, ['onChange', 'onBlur']);

  return (
    <FormattedTextField
      id={idValue}
      filter={filter}
      error={hasError}
      helperText={helperTextValue as string}
      onChange={handleChange}
      onBlur={handleBlur}
      {...trimmedFieldConfig}
      {...rest}
    />
  );
};

export default FormFormattedTextField;
